/**
 * IRCC Admin Component (Redesigned)
 * 
 * Experts manage IRCC forms from CRM database (not directly from IRCC).
 * - View available forms from our database
 * - Create templates from forms
 * - Check for updates (database vs local templates)
 * 
 * Flow: CRM syncs with IRCC → Database → Expert creates templates
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { FaSync, FaCheck, FaTimes, FaPlus, FaExclamationTriangle, FaClock, FaDatabase, FaFileAlt, FaArrowUp, FaDownload } from 'react-icons/fa';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const Container = styled.div`
  animation: ${fadeIn} 0.3s ease;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 16px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.$color || '#a5b4fc'};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoBox = styled.div`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 20px;
`;

const InfoTitle = styled.h3`
  margin: 0 0 12px 0;
  color: #e2e8f0;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InfoText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.6;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: ${props => props.$primary 
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
    : 'rgba(30, 41, 59, 0.8)'};
  border: 1px solid ${props => props.$primary ? 'transparent' : 'rgba(99, 102, 241, 0.3)'};
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    ${props => props.$spinning && css`animation: ${spin} 1s linear infinite;`}
  }
`;

const Badge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => {
    switch(props.$type) {
      case 'available': return 'rgba(16, 185, 129, 0.2)';
      case 'hasTemplate': return 'rgba(59, 130, 246, 0.2)';
      case 'needsUpdate': return 'rgba(245, 158, 11, 0.2)';
      default: return 'rgba(107, 114, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch(props.$type) {
      case 'available': return '#34d399';
      case 'hasTemplate': return '#60a5fa';
      case 'needsUpdate': return '#fbbf24';
      default: return '#9ca3af';
    }
  }};
  border: 1px solid currentColor;
`;

const FormsGrid = styled.div`
  display: grid;
  gap: 12px;
`;

const FormCard = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: rgba(15, 23, 42, 0.4);
  border: 1px solid ${props => props.$needsUpdate 
    ? 'rgba(245, 158, 11, 0.4)' 
    : 'rgba(99, 102, 241, 0.15)'};
  border-radius: 12px;
  gap: 16px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.3);
  }
`;

const FormIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: ${props => props.$hasTemplate 
    ? 'rgba(59, 130, 246, 0.2)' 
    : 'rgba(107, 114, 128, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$hasTemplate ? '#60a5fa' : '#9ca3af'};
  font-size: 20px;
`;

const FormInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FormCode = styled.div`
  font-family: 'Monaco', 'Consolas', monospace;
  font-weight: 600;
  color: #a5b4fc;
  font-size: 14px;
  margin-bottom: 4px;
`;

const FormName = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FormMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
`;

const FormActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%);
  border: 1px solid rgba(16, 185, 129, 0.4);
  border-radius: 6px;
  color: #34d399;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(16, 185, 129, 0.2) 100%);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const UpdateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%);
  border: 1px solid rgba(245, 158, 11, 0.4);
  border-radius: 6px;
  color: #fbbf24;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.3) 0%, rgba(245, 158, 11, 0.2) 100%);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(99, 102, 241, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.4);
  border-radius: 6px;
  color: #a5b4fc;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(99, 102, 241, 0.2) 100%);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${props => props.$type === 'success' 
    ? 'rgba(16, 185, 129, 0.15)' 
    : props.$type === 'error'
    ? 'rgba(239, 68, 68, 0.15)'
    : 'rgba(59, 130, 246, 0.15)'};
  color: ${props => props.$type === 'success' 
    ? '#34d399' 
    : props.$type === 'error'
    ? '#f87171'
    : '#60a5fa'};
  border: 1px solid currentColor;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.5);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(99, 102, 241, 0.2);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 40px auto;
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const FilterTab = styled.button`
  padding: 8px 16px;
  background: ${props => props.$active
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    : 'rgba(30, 41, 59, 0.8)'};
  border: 1px solid ${props => props.$active ? 'transparent' : 'rgba(99, 102, 241, 0.3)'};
  border-radius: 8px;
  color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active
      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
      : 'rgba(99, 102, 241, 0.2)'};
    color: white;
  }
`;

// Confirmation Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ModalContent = styled.div`
  background: #1e293b;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  border: 1px solid rgba(99, 102, 241, 0.3);
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px 0;
  color: #e2e8f0;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ModalText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 16px;
`;

const ModalWarning = styled.div`
  background: rgba(245, 158, 11, 0.15);
  border: 1px solid rgba(245, 158, 11, 0.4);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  color: #fbbf24;
  font-size: 13px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

// ============================================
// COMPONENT
// ============================================

const IRCCAdmin = () => {
  const axios = useAuthenticatedAxios();
  
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [forms, setForms] = useState([]);
  const [stats, setStats] = useState({ totalAvailable: 0, withTemplates: 0, needingUpdate: 0 });
  const [message, setMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // all, available, hasTemplate, needsUpdate
  const [creatingTemplate, setCreatingTemplate] = useState(null);
  const [downloadingForm, setDownloadingForm] = useState(null);
  const [lastDatabaseUpdate, setLastDatabaseUpdate] = useState(null);
  
  // Update confirmation modal
  const [updateConfirm, setUpdateConfirm] = useState(null); // { form, activeSessions, warning }
  const [checkingUsage, setCheckingUsage] = useState(null);
  
  // Load forms from database
  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/ircc/database/forms');
      if (response.data.success) {
        const formsData = response.data.forms || [];
        setForms(formsData);
        setStats(response.data.stats || { totalAvailable: 0, withTemplates: 0, needingUpdate: 0 });
        
        // Find most recent update in database
        const latestUpdate = formsData.reduce((latest, form) => {
          if (form.lastUpdated && (!latest || new Date(form.lastUpdated) > new Date(latest))) {
            return form.lastUpdated;
          }
          return latest;
        }, null);
        setLastDatabaseUpdate(latestUpdate);
      }
    } catch (error) {
      console.error('Error loading forms:', error);
      setMessage({ type: 'error', text: 'Failed to load forms from database' });
    } finally {
      setLoading(false);
    }
  }, [axios]);
  
  useEffect(() => {
    loadForms();
  }, [loadForms]);
  
  // Check for updates
  const checkForUpdates = async () => {
    setChecking(true);
    setMessage(null);
    
    try {
      const response = await axios.get('/ircc/database/check-updates');
      if (response.data.success) {
        const { summary, message: updateMessage } = response.data;
        setMessage({ 
          type: summary.needingUpdate > 0 ? 'warning' : 'success', 
          text: updateMessage 
        });
        // Reload forms to get updated status
        await loadForms();
      }
    } catch (error) {
      console.error('Check updates error:', error);
      setMessage({ type: 'error', text: 'Failed to check for updates' });
    } finally {
      setChecking(false);
    }
  };
  
  // Create template from form
  const createTemplate = async (form, forceUpdate = false) => {
    setCreatingTemplate(form.formId);
    setMessage(null);
    
    try {
      const response = await axios.post('/document-templates/import-ircc', {
        formId: form.formId,
        forceUpdate
      });
      
      if (response.data.success) {
        const action = response.data.updated ? 'Updated' : 'Created';
        setMessage({ 
          type: 'success', 
          text: `${action} template for ${form.formCode}! You can now use it in your cases.` 
        });
        // Reload forms to update status
        await loadForms();
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Failed to create template' });
      }
    } catch (error) {
      console.error('Create template error:', error);
      const errorMsg = error.response?.data?.error || 'Failed to create template';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setCreatingTemplate(null);
    }
  };
  
  // Check template usage before updating
  const checkBeforeUpdate = async (form) => {
    if (!form.templateId) {
      // No existing template, just create
      await createTemplate(form, false);
      return;
    }
    
    setCheckingUsage(form.formId);
    
    try {
      const response = await axios.get(`/ircc/database/template-usage/${form.templateId}`);
      
      if (response.data.success) {
        // Show confirmation modal with info
        setUpdateConfirm({
          form,
          activeSessions: response.data.activeSessions,
          warning: response.data.warning,
          templateInfo: response.data.template
        });
      }
    } catch (error) {
      console.error('Check usage error:', error);
      // If check fails, still show basic confirmation
      setUpdateConfirm({
        form,
        activeSessions: { count: 0, sessions: [] },
        warning: null
      });
    } finally {
      setCheckingUsage(null);
    }
  };
  
  // Actually perform the update after confirmation
  const confirmUpdate = async () => {
    if (!updateConfirm) return;
    
    const form = updateConfirm.form;
    setUpdateConfirm(null);
    await createTemplate(form, true); // forceUpdate = true
  };
  
  // Cancel update
  const cancelUpdate = () => {
    setUpdateConfirm(null);
  };
  
  // Download form PDF from database
  const downloadForm = async (form) => {
    setDownloadingForm(form.formId);
    
    try {
      const response = await axios.get(`/ircc/database/download/${form.formId}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${form.formCode.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: `Downloaded ${form.formCode}` });
    } catch (error) {
      console.error('Download error:', error);
      setMessage({ type: 'error', text: 'Failed to download form' });
    } finally {
      setDownloadingForm(null);
    }
  };
  
  // Filter forms
  const filteredForms = forms.filter(form => {
    switch (filter) {
      case 'available':
        return !form.hasTemplate;
      case 'hasTemplate':
        return form.hasTemplate;
      case 'needsUpdate':
        return form.needsUpdate;
      default:
        return true;
    }
  });
  
  // Clear message after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);
  
  return (
    <Container>
      {/* Info Box */}
      <InfoBox>
        <InfoTitle>
          <FaDatabase style={{ color: '#a5b4fc' }} />
          IRCC Forms Database
        </InfoTitle>
        <InfoText>
          Forms are managed centrally in our database. Create templates from available forms 
          to use them in your cases. Check for updates periodically to ensure you have the 
          latest versions.
        </InfoText>
        {lastDatabaseUpdate && (
          <div style={{ marginTop: 12, fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FaClock />
            Database last updated: {new Date(lastDatabaseUpdate).toLocaleDateString()} at {new Date(lastDatabaseUpdate).toLocaleTimeString()}
          </div>
        )}
      </InfoBox>
      
      {/* Stats */}
      <StatsGrid>
        <StatCard>
          <StatValue>{stats.totalAvailable}</StatValue>
          <StatLabel>Available</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue $color="#60a5fa">{stats.withTemplates}</StatValue>
          <StatLabel>Templates</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue $color="#fbbf24">{stats.needingUpdate}</StatValue>
          <StatLabel>Need Update</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue $color="#34d399">
            {stats.totalAvailable > 0 
              ? Math.round((stats.withTemplates / stats.totalAvailable) * 100) 
              : 0}%
          </StatValue>
          <StatLabel>Coverage</StatLabel>
        </StatCard>
      </StatsGrid>
      
      {/* Actions */}
      <ActionBar>
        <Button onClick={loadForms} disabled={loading} $spinning={loading}>
          <FaSync />
          Refresh
        </Button>
        <Button $primary onClick={checkForUpdates} disabled={checking} $spinning={checking}>
          <FaSync />
          {checking ? 'Checking...' : 'Check for Updates'}
        </Button>
      </ActionBar>
      
      {/* Message */}
      {message && (
        <Message $type={message.type}>
          {message.type === 'success' && <FaCheck />}
          {message.type === 'error' && <FaTimes />}
          {message.type === 'warning' && <FaExclamationTriangle />}
          {message.type === 'info' && <FaExclamationTriangle />}
          {message.text}
        </Message>
      )}
      
      {/* Filter Tabs */}
      <FilterTabs>
        <FilterTab $active={filter === 'all'} onClick={() => setFilter('all')}>
          All ({forms.length})
        </FilterTab>
        <FilterTab $active={filter === 'available'} onClick={() => setFilter('available')}>
          Available ({forms.filter(f => !f.hasTemplate).length})
        </FilterTab>
        <FilterTab $active={filter === 'hasTemplate'} onClick={() => setFilter('hasTemplate')}>
          With Templates ({forms.filter(f => f.hasTemplate).length})
        </FilterTab>
        <FilterTab $active={filter === 'needsUpdate'} onClick={() => setFilter('needsUpdate')}>
          Needs Update ({forms.filter(f => f.needsUpdate).length})
        </FilterTab>
      </FilterTabs>
      
      {/* Forms List */}
      {loading ? (
        <LoadingSpinner />
      ) : filteredForms.length === 0 ? (
        <EmptyState>
          <p>No forms found.</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>
            {filter !== 'all' ? 'Try changing the filter.' : 'Contact admin to upload forms to the database.'}
          </p>
        </EmptyState>
      ) : (
        <FormsGrid>
          {filteredForms.map(form => (
            <FormCard key={form.formId} $needsUpdate={form.needsUpdate}>
              <FormIcon $hasTemplate={form.hasTemplate}>
                <FaFileAlt />
              </FormIcon>
              
              <FormInfo>
                <FormCode>{form.formCode}</FormCode>
                <FormName title={form.formName}>{form.formName}</FormName>
                <FormMeta>
                  <span>v{form.databaseVersion}</span>
                  {form.lastUpdated && (
                    <>
                      <span>•</span>
                      <span><FaClock style={{ marginRight: 4 }} />{new Date(form.lastUpdated).toLocaleDateString()}</span>
                    </>
                  )}
                  {form.hasTemplate && (
                    <>
                      <span>•</span>
                      <Badge $type="hasTemplate">Template v{form.templateVersion || 1}</Badge>
                    </>
                  )}
                  {form.needsUpdate && (
                    <>
                      <span>•</span>
                      <Badge $type="needsUpdate">Update Available</Badge>
                    </>
                  )}
                </FormMeta>
              </FormInfo>
              
              <FormActions>
                {/* Download Button - always available */}
                <DownloadButton
                  onClick={() => downloadForm(form)}
                  disabled={downloadingForm === form.formId}
                  title="Download PDF from database"
                >
                  <FaDownload size={11} />
                  {downloadingForm === form.formId ? '...' : 'PDF'}
                </DownloadButton>
                
                {form.needsUpdate ? (
                  <UpdateButton 
                    onClick={() => checkBeforeUpdate(form)}
                    disabled={creatingTemplate === form.formId || checkingUsage === form.formId}
                  >
                    <FaArrowUp size={11} />
                    {creatingTemplate === form.formId ? 'Updating...' : checkingUsage === form.formId ? 'Checking...' : 'Update'}
                  </UpdateButton>
                ) : !form.hasTemplate ? (
                  <CreateButton 
                    onClick={() => createTemplate(form)}
                    disabled={creatingTemplate === form.formId}
                  >
                    <FaPlus size={11} />
                    {creatingTemplate === form.formId ? 'Creating...' : 'Create Template'}
                  </CreateButton>
                ) : (
                  <Badge $type="available">
                    <FaCheck style={{ marginRight: 4 }} />
                    Up to date
                  </Badge>
                )}
              </FormActions>
            </FormCard>
          ))}
        </FormsGrid>
      )}
      
      {/* Update Confirmation Modal */}
      {updateConfirm && (
        <ModalOverlay onClick={cancelUpdate}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>
              <FaExclamationTriangle style={{ color: '#fbbf24' }} />
              Update Template?
            </ModalTitle>
            
            <ModalText>
              You are about to update <strong>{updateConfirm.form.formCode}</strong> from 
              version {updateConfirm.form.templateVersion || 1} to version {updateConfirm.form.databaseVersion}.
            </ModalText>
            
            <ModalText>
              <strong>What will happen:</strong>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>The template fields will be updated to match the new form</li>
                <li>The questionnaire will be regenerated with AI</li>
                <li>New form filling sessions will use the updated version</li>
              </ul>
            </ModalText>
            
            {updateConfirm.activeSessions?.count > 0 && (
              <ModalWarning>
                <FaExclamationTriangle />
                <div>
                  <strong>{updateConfirm.activeSessions.count} active session(s)</strong> are using this template.
                  Their collected data will be preserved, but they will continue using the old version until completed.
                </div>
              </ModalWarning>
            )}
            
            <ModalActions>
              <Button onClick={cancelUpdate}>
                Cancel
              </Button>
              <Button $primary onClick={confirmUpdate}>
                Yes, Update Template
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default IRCCAdmin;
